import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Heart, X } from "lucide-react";

interface GoodDeed {
  id: string;
  text: string;
  day: number;
  timestamp: number;
}

interface GoodDeedsTrackerProps {
  deeds: GoodDeed[];
  currentDay: number;
  onAddDeed: (text: string, day: number) => void;
  onRemoveDeed: (id: string) => void;
}

export const GoodDeedsTracker = ({ 
  deeds, 
  currentDay,
  onAddDeed, 
  onRemoveDeed 
}: GoodDeedsTrackerProps) => {
  const [newDeed, setNewDeed] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const todayDeeds = deeds.filter(d => d.day === currentDay);
  const totalDeeds = deeds.length;

  const handleAdd = () => {
    if (newDeed.trim()) {
      onAddDeed(newDeed.trim(), currentDay);
      setNewDeed("");
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
          Day {currentDay}
        </span>
        <span className="text-xs text-muted-foreground">
          <Heart className="w-3 h-3 inline mr-1" />
          {totalDeeds} total good deeds
        </span>
      </div>

      {/* Today's deeds */}
      <div className="space-y-2 min-h-[80px]">
        <AnimatePresence>
          {todayDeeds.length === 0 && !isAdding ? (
            <p className="text-sm text-muted-foreground italic text-center py-4">
              No good deeds logged for today yet
            </p>
          ) : (
            todayDeeds.map((deed) => (
              <motion.div
                key={deed.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex items-center gap-3 bg-background border border-border rounded-sm p-3 group"
              >
                <Heart className="w-4 h-4 text-gold flex-shrink-0" />
                <span className="flex-1 text-sm text-foreground/85">{deed.text}</span>
                <button
                  onClick={() => onRemoveDeed(deed.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Add new deed */}
      {isAdding ? (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="space-y-2"
        >
          <input
            type="text"
            value={newDeed}
            onChange={(e) => setNewDeed(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            placeholder="Helped someone, gave charity, smiled..."
            className="w-full px-4 py-3 bg-background border border-border rounded-sm text-sm 
                       focus:outline-none focus:border-gold transition-colors"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              className="flex-1 py-2 text-sm bg-gold text-white rounded-sm hover:bg-gold/90 transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => setIsAdding(false)}
              className="px-4 py-2 text-sm border border-border rounded-sm hover:bg-accent transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      ) : (
        <motion.button
          onClick={() => setIsAdding(true)}
          className="w-full py-3 border border-dashed border-border rounded-sm text-sm text-muted-foreground
                     hover:border-gold/50 hover:text-gold transition-colors flex items-center justify-center gap-2"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <Plus className="w-4 h-4" />
          Log a good deed
        </motion.button>
      )}
    </div>
  );
};
